import { useEffect, useState } from 'react';

/**
 * Viewer Count — v2 port of v1's viewer-count.ejs / kick-viewer-count.ejs / youtube-viewer-count.ejs.
 *
 * Polls `/api/streamers/:handle/live-status` (returns counts for all three platforms).
 * Renders one card per enabled platform. Shows OFFLINE per-platform if showOfflineState=true.
 *
 * Config:
 *   platforms: {
 *     twitch:  { enabled: boolean; handle: string }
 *     youtube: { enabled: boolean; channelId: string }
 *     kick:    { enabled: boolean; username: string }
 *   }
 *   layout: 'row' | 'stacked'           (default 'row')
 *   label: string                       (default 'Viewers')
 *   showOfflineState: boolean           (default true)
 *   pollIntervalSec: number             (default 30)
 *   handle: string                      (legacy v1 field — streamer handle for API lookup)
 */

type PlatformKey = 'twitch' | 'youtube' | 'kick';

interface PlatformStatus {
  isLive: boolean;
  viewers: number;
  title: string | null;
}

interface AggregatedStatus {
  twitch: PlatformStatus;
  youtube: PlatformStatus;
  kick: PlatformStatus;
}

interface PlatformsConfig {
  twitch?: { enabled?: boolean; handle?: string };
  youtube?: { enabled?: boolean; channelId?: string };
  kick?: { enabled?: boolean; username?: string };
}

const PLATFORM_COLOR: Record<PlatformKey, string> = {
  twitch: '#9147ff',
  youtube: '#ff0000',
  kick: '#53fc18',
};

const PLATFORM_ORDER: PlatformKey[] = ['twitch', 'youtube', 'kick'];

const OFFLINE: PlatformStatus = { isLive: false, viewers: 0, title: null };
const EMPTY: AggregatedStatus = { twitch: OFFLINE, youtube: OFFLINE, kick: OFFLINE };

function formatCount(n: number): string {
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(1).replace(/\.0$/, '') + 'M';
  if (n >= 10_000) return (n / 1_000).toFixed(1).replace(/\.0$/, '') + 'K';
  return n.toLocaleString();
}

function PlatformIcon({ platform, color }: { platform: PlatformKey; color: string }) {
  if (platform === 'youtube') {
    return (
      <svg width={22} height={22} viewBox="0 0 24 24" fill={color}>
        <path d="M23.498 6.186a3 3 0 00-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3 3 0 00.502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3 3 0 002.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3 3 0 002.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
      </svg>
    );
  }
  if (platform === 'kick') {
    return (
      <svg width={22} height={22} viewBox="0 0 22.3 26" fill={color}>
        <path d="M0 0H8.341V5.794H11.116V2.912H13.892V0H22.233V8.693H19.457V11.575H16.682V14.456H19.457V17.338H22.233V26H13.892V23.118H11.116V20.237H8.341V26H0V0Z" />
      </svg>
    );
  }
  return (
    <svg width={22} height={22} viewBox="0 0 24 24" fill={color}>
      <path d="M11.571 4.714h1.715v5.143H11.57zm4.715 0H18v5.143h-1.714zM6 0L1.714 4.286v15.428h5.143V24l4.286-4.286h3.428L22.286 12V0zm14.571 11.143l-3.428 3.428h-3.429l-3 3v-3H6.857V1.714h13.714z" />
    </svg>
  );
}

function PlatformCard({
  platform,
  status,
  label,
  bumpTick,
}: {
  platform: PlatformKey;
  status: PlatformStatus;
  label: string;
  bumpTick: number;
}) {
  const color = PLATFORM_COLOR[platform];
  const isLive = status.isLive;
  const [bump, setBump] = useState(false);

  useEffect(() => {
    if (bumpTick === 0) return;
    setBump(true);
    const t = setTimeout(() => setBump(false), 450);
    return () => clearTimeout(t);
  }, [bumpTick]);

  return (
    <div
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 12,
        background: 'linear-gradient(135deg, rgba(15,12,30,0.93), rgba(22,16,46,0.93))',
        border: `1px solid ${isLive ? `${color}66` : 'rgba(139,92,246,0.22)'}`,
        borderRadius: 14,
        padding: '12px 18px 12px 14px',
        minWidth: 200,
        position: 'relative',
        overflow: 'hidden',
        boxShadow: isLive
          ? `0 8px 32px rgba(0,0,0,0.55), 0 0 18px ${color}33`
          : '0 8px 32px rgba(0,0,0,0.55)',
        color: 'white',
        fontFamily: "'Poppins', system-ui, sans-serif",
        transition: 'border-color 500ms, box-shadow 500ms',
      }}
    >
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 16,
          right: 16,
          height: 1,
          background: `linear-gradient(90deg, transparent, ${color}80, transparent)`,
        }}
      />
      <span
        style={{
          width: 8,
          height: 8,
          borderRadius: '50%',
          background: isLive ? '#ef4444' : '#6b7280',
          flexShrink: 0,
          animation: isLive ? 'vc-pulse 2s ease-in-out infinite' : undefined,
        }}
      />
      <PlatformIcon platform={platform} color={color} />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 1, minWidth: 0 }}>
        <div
          style={{
            fontSize: 9,
            fontWeight: 700,
            letterSpacing: 1.2,
            textTransform: 'uppercase',
            color: 'rgba(255,255,255,0.38)',
            lineHeight: 1,
          }}
        >
          {label}
        </div>
        {isLive ? (
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 5 }}>
            <span
              style={{
                fontSize: 26,
                fontWeight: 800,
                lineHeight: 1.1,
                fontVariantNumeric: 'tabular-nums',
                color: bump ? color : 'white',
                transform: bump ? 'scale(1.15)' : 'scale(1)',
                transition: 'transform 200ms cubic-bezier(0.34,1.56,0.64,1), color 200ms',
              }}
            >
              {formatCount(status.viewers)}
            </span>
            <span
              style={{
                fontSize: 10,
                fontWeight: 600,
                color: 'rgba(255,255,255,0.3)',
                textTransform: 'uppercase',
                letterSpacing: 0.5,
              }}
            >
              watching
            </span>
          </div>
        ) : (
          <span
            style={{
              fontSize: 15,
              fontWeight: 700,
              color: 'rgba(255,255,255,0.3)',
              letterSpacing: 0.5,
            }}
          >
            OFFLINE
          </span>
        )}
      </div>
    </div>
  );
}

export function ViewerCountWidget({ fields }: { fields: Record<string, unknown> }) {
  const platforms = (fields.platforms as PlatformsConfig | undefined) ?? {};
  const layout = ((fields.layout as string | undefined) ?? 'row') === 'stacked' ? 'stacked' : 'row';
  const label = String(fields.label ?? 'Viewers');
  const showOfflineState = (fields.showOfflineState as boolean | undefined) ?? true;
  const pollIntervalSec = Number(fields.pollIntervalSec ?? 30);
  // Legacy v1 single-handle field still used as the lookup key.
  const legacyHandle = String(fields.handle ?? '');

  const [status, setStatus] = useState<AggregatedStatus>(EMPTY);
  // Per-platform tick that increments when viewers change → child uses it to bump.
  const [bumpTick, setBumpTick] = useState<Record<PlatformKey, number>>({
    twitch: 0,
    youtube: 0,
    kick: 0,
  });

  const twitchEnabled = !!platforms.twitch?.enabled;
  const youtubeEnabled = !!platforms.youtube?.enabled;
  const kickEnabled = !!platforms.kick?.enabled;
  const anyEnabled = twitchEnabled || youtubeEnabled || kickEnabled;

  useEffect(() => {
    if (!legacyHandle || !anyEnabled) return;
    let cancelled = false;
    const poll = async () => {
      try {
        const res = await fetch(`/api/streamers/${encodeURIComponent(legacyHandle)}/live-status`);
        if (!res.ok) {
          if (!cancelled) setStatus(EMPTY);
          return;
        }
        const next = (await res.json()) as AggregatedStatus;
        if (cancelled) return;
        setStatus((prev) => {
          const tick = { ...bumpTick };
          for (const key of PLATFORM_ORDER) {
            if (prev[key].viewers !== next[key].viewers && next[key].isLive) {
              tick[key] = tick[key] + 1;
            }
          }
          setBumpTick(tick);
          return next;
        });
      } catch {
        if (!cancelled) setStatus(EMPTY);
      }
    };
    void poll();
    const t = setInterval(poll, pollIntervalSec * 1000);
    return () => {
      cancelled = true;
      clearInterval(t);
    };
    // bumpTick intentionally excluded — we read+write it inside the poller without
    // wanting to restart the interval on every change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [legacyHandle, anyEnabled, pollIntervalSec]);

  if (!legacyHandle) {
    return (
      <div
        style={{
          padding: '12px 18px',
          fontFamily: "'Poppins', system-ui, sans-serif",
          color: '#f87171',
          fontSize: 12,
          background: 'rgba(15,12,30,0.85)',
          border: '1px solid rgba(248,113,113,0.3)',
          borderRadius: 14,
        }}
      >
        Set `handle` field to streamer handle
      </div>
    );
  }

  if (!anyEnabled) {
    return (
      <div
        style={{
          padding: '12px 18px',
          fontFamily: "'Poppins', system-ui, sans-serif",
          color: 'rgba(255,255,255,0.4)',
          fontSize: 12,
          background: 'rgba(15,12,30,0.85)',
          border: '1px solid rgba(139,92,246,0.18)',
          borderRadius: 14,
        }}
      >
        Enable at least one platform
      </div>
    );
  }

  const visible: PlatformKey[] = PLATFORM_ORDER.filter((p) => {
    const enabled =
      (p === 'twitch' && twitchEnabled) ||
      (p === 'youtube' && youtubeEnabled) ||
      (p === 'kick' && kickEnabled);
    if (!enabled) return false;
    if (!showOfflineState && !status[p].isLive) return false;
    return true;
  });

  if (visible.length === 0) return null;

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: layout === 'stacked' ? 'column' : 'row',
        gap: 10,
        // nowrap keeps the row strictly horizontal — Kick was dropping to a
        // second line on browser sources narrower than ~620px (3 cards ×
        // 200 minWidth + 2 × 10 gap). Streamers set the OBS source width
        // to fit; we don't second-guess.
        flexWrap: 'nowrap',
      }}
    >
      {visible.map((p) => (
        <PlatformCard
          key={p}
          platform={p}
          status={status[p]}
          label={label}
          bumpTick={bumpTick[p]}
        />
      ))}
      <style>{`
        @keyframes vc-pulse {
          0%, 100% { box-shadow: 0 0 0 0 rgba(239,68,68,0.5); }
          50% { box-shadow: 0 0 0 5px rgba(239,68,68,0); }
        }
      `}</style>
    </div>
  );
}
